// ─── agent_messages.status → how a transcript row should be drawn ──────────────
//
// WHY THIS EXISTS
// The Conversations transcript used to render every agent_messages row as a sent
// bubble. It is not one. Since 2026-07-22 the column carries a real lifecycle
// (recruiter-agent/db/2026-07-22-agent-messages-status.sql):
//
//   NULL        an ordinary logged turn — every pre-existing row, and every
//               normal immediate send. NOT a queue entry.
//   'queued'    drafted during quiet hours, parked for the send window.
//   'sending'   claimed by a cron invocation, in flight.
//   'sent'      handed to Twilio; twilio_sid filled in.
//   'failed'    Twilio rejected it. NOTHING REACHED THE CANDIDATE.
//   'cancelled' dropped before sending — stale >24h, opted out overnight, a
//               recruiter took the thread over, or the row had no phone.
//   'no_reply'  (2026-09-24, not yet written by the worker) the agent read the
//               message and DECLARED that it needs no answer. Nothing was
//               offered to Twilio. See recruiter-agent
//               db/2026-09-24-no-reply-status.sql for the landing order: this
//               classifier first, the migration second, the writer last.
//   'handled_offline'  (2026-09-25, not yet written by the worker) a role='human'
//               row with body [handled-offline]: a recruiter marked the thread as
//               handled OFF-SYSTEM (texted or called from their own phone). Nothing
//               was sent by us. It is neither delivered nor failed -- it has its own
//               kind, 'offline'. Same landing order as no_reply: this classifier,
//               then recruiter-agent db/2026-09-25-handled-offline-status.sql, then
//               the writer (recruiter-agent POST /staff-note).
//
// Two of those never reached a human being, and the worker says so in as many
// words at recruiter-agent/src/index.js:1657: "a 'failed' row is an attempt, not
// a delivered message. A reader that shows every row regardless of status will
// show it as though the candidate received it." That reader was this CRM.
//
// The cost of getting it wrong is a recruiter opening a thread, seeing their own
// rejected text sitting there like any other, and waiting for a reply to a
// message that was never delivered.
//
// PURE ON PURPOSE. The mapping lives here rather than inline in App.jsx so it can
// be unit-tested without a DOM — this repo has jest via react-scripts but no
// @testing-library, so a pure function is the only thing that can be pinned.

// The two values below are NOT in the 2026-07-22 CHECK constraint and cannot be
// written today. They are Twilio's own delivery-callback vocabulary, and are
// mapped here so that if a status callback is ever wired up (the obvious next
// step for this column) it degrades correctly instead of silently rendering an
// undelivered message as delivered. Costs nothing; forecloses a repeat of the
// exact bug this module fixes.
const NOT_DELIVERED = new Set(['failed', 'undelivered', 'cancelled', 'no_reply']);
const IN_FLIGHT = new Set(['queued', 'sending']);
export const HANDLED_OFFLINE = 'handled_offline';

/**
 * Classify one agent_messages row's status.
 *
 * Returns { kind, label, delivered } where kind is:
 *   'ok'       — treat exactly as before: a normal bubble, no annotation.
 *   'pending'  — written, but nobody has received it yet.
 *   'failed'   — an attempt that did not land. Must never look like 'ok'.
 *   'offline'  — a recruiter handled the thread off-system. Not a message we sent,
 *                so neither 'ok' (delivered) nor 'failed'.
 *
 * Unrecognised and missing values fall through to 'ok', which is today's
 * behaviour. That is deliberate: the CHECK constraint means an unknown value
 * cannot currently exist, and defaulting a future benign value to a red
 * "not delivered" bubble would be a worse failure than defaulting it to normal.
 */
export function deliveryStateOf(status) {
  const s = String(status == null ? '' : status).trim().toLowerCase();

  if (NOT_DELIVERED.has(s)) {
    return {
      kind: 'failed',
      // 'cancelled' is named separately because it is not a rejection — the
      // agent withdrew the draft — and a recruiter reading the thread needs to
      // know nobody rejected anything before they go chasing a carrier.
      //
      // 'no_reply' is named for what it is: the agent chose not to answer. It is
      // not-delivered (nothing was sent), but it is neither a carrier failure nor
      // a withdrawn draft, and a recruiter should read it as a decision to review.
      label: s === 'cancelled' ? 'not sent — cancelled'
        : s === 'no_reply' ? 'no reply — declared by the agent'
        : 'not delivered',
      delivered: false,
    };
  }

  if (IN_FLIGHT.has(s)) {
    return {
      kind: 'pending',
      label: s === 'sending' ? 'sending…' : 'scheduled — quiet hours',
      delivered: false,
    };
  }

  if (s === HANDLED_OFFLINE) {
    return { kind: 'offline', label: 'handled off-system', delivered: false };
  }

  // 'sent', 'delivered', NULL, '', and anything unrecognised.
  return { kind: 'ok', label: '', delivered: true };
}

/**
 * Inline styles for a transcript bubble, given the delivery state and which side
 * of the thread it sits on. Kept beside the classifier so the "never a sent
 * bubble" rule and the colours that enforce it cannot drift apart.
 *
 * `isStaff` / `isAgent` carry the existing our-side colouring; a non-'ok' state
 * overrides both, because "did this land?" outranks "who wrote it?".
 */
export function bubbleStyleFor(state, { isStaff, isAgent }) {
  const base = {
    borderRadius: 14,
    borderBottomRightRadius: isAgent ? 4 : 14,
    borderBottomLeftRadius: isAgent ? 14 : 4,
    padding: '9px 13px',
    fontSize: 13.5,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  if (state.kind === 'failed') {
    // Dashed, unfilled, red. Deliberately the least bubble-like thing on the
    // page: at a glance it reads as an artefact, not as a message someone got.
    return { ...base, background: '#fff1f2', color: '#9f1239', border: '1px dashed #fb7185' };
  }

  if (state.kind === 'pending') {
    // Dashed amber — written and intact, but still ours, not theirs.
    return { ...base, background: '#fffbeb', color: '#92400e', border: '1px dashed #fcd34d' };
  }

  if (state.kind === 'offline') {
    // Dotted slate — a note, not a message: nothing was sent from here.
    return { ...base, background: '#f8fafc', color: '#334155', border: '1px dotted #94a3b8', fontStyle: 'italic' };
  }

  return {
    ...base,
    background: isStaff ? '#b45309' : isAgent ? '#1d4ed8' : '#f1f5f9',
    color: isAgent ? 'white' : '#1e293b',
  };
}

/**
 * The meta-line badge for a non-'ok' state: { icon, color }. App.jsx used to pick
 * between two looks inline ('failed' red, everything else amber), which would have
 * drawn a third kind as "scheduled". Kept here so every kind is pinned by a test.
 */
export function stateBadge(state) {
  if (!state || state.kind === 'ok') return null;
  if (state.kind === 'failed') return { icon: '⚠ ', color: '#9f1239' };
  if (state.kind === 'offline') return { icon: '✓ ', color: '#334155' };
  return { icon: '🕒 ', color: '#92400e' };
}
